import { styled as muiStyled, css, darken } from '@mui/material/styles';
import React, { Suspense } from 'react';
import {
  Navigate,
  useLocation,
  RouteObject,
  generatePath,
  useNavigate,
  useParams,
  NavigateOptions,
  Link as RRLink,
  LinkProps as RRLinkProps,
} from 'react-router-dom';

// Dashboard is eager-loaded to prevent Suspense fallback flash on navigation
import { Dashboard as ProjectDashboard } from './components/project/dashboard';
import { lazyWithReload as lazy } from './lazy-with-reload';

import {
  CapabilityAuth,
  RequireCapability,
} from '~/client/components/capability-auth';
import { ProjectType } from '~/graphql/generated/client';
import AuthRoute from '~c/components/auth-route';
import { DesignToken } from '~c/components/base/core';
import { LoadingFallback } from '~c/components/base/loading-fallback';
import {
  Compliance as DashboardCompliance,
  Tasks as DashboardTasks,
} from '~c/components/dashboard';
import ComplianceProjects from '~c/components/dashboard/compliance/projects-view';
import DashboardOrganization from '~c/components/dashboard/organization';
import ResponsibleAITraining from '~c/components/dashboard/responsible-ai-training';
import Landing from '~c/components/landing';

// ============================================================================
// LAZY-LOADED COMPONENTS
// These components are loaded on-demand to reduce initial bundle size.
// Group by feature for better code organization.
// ============================================================================

// --- Admin Components ---
const OrgAdminDashboard = lazy(
  () => import('./components/admin/org-admin-dashboard')
);
const SystemAdminDashboard = lazy(
  () => import('./components/admin/system-admin-dashboard')
);
const GuardianOAuthAuthorize = lazy(
  () => import('./components/guardian-oauth/authorize')
);
const ReportTemplateInit = lazy(() =>
  import('./components/report-template-init').then((m) => ({
    default: m.ReportTemplateInit,
  }))
);

// --- Assessment Components ---
const Assessment = {
  Landing: lazy(() =>
    import('./components/assessment').then((m) => ({ default: m.Landing }))
  ),
  GettingStarted: lazy(() =>
    import('./components/assessment').then((m) => ({
      default: m.GettingStarted,
    }))
  ),
  BundleForm: lazy(() =>
    import('./components/assessment').then((m) => ({ default: m.BundleForm }))
  ),
  RiskOverTime: lazy(() =>
    import('./components/assessment').then((m) => ({ default: m.RiskOverTime }))
  ),
  SubmitForReview: lazy(() =>
    import('./components/assessment').then((m) => ({
      default: m.SubmitForReview,
    }))
  ),
  AssessProject: lazy(() =>
    import('./components/assessment').then((m) => ({
      default: m.AssessProject,
    }))
  ),
};
const ModelCardAssessment = lazy(
  () => import('./components/assessment/model-card-assessement')
);

// --- Dashboard Components ---
// Compliance, Tasks, Organization, ResponsibleAITraining are eager-loaded above for instant dashboard tab switching
const DashboardStatus = lazy(() => import('./components/dashboard/status'));
const AsenionGenAI = lazy(() => import('./components/dashboard/asenion-genai'));
const AssuranceHub = lazy(() => import('./components/dashboard/assurance'));
const AssuranceLibrary = lazy(
  () => import('./components/dashboard/governance/assurance-library')
);
const ControlBundle = lazy(
  () => import('./components/dashboard/governance/policies/control-bundle')
);
const PolicyDetails = lazy(
  () => import('./components/dashboard/governance/policies/policy-details')
);
const PreviousPolicies = lazy(
  () => import('./components/dashboard/governance/policies/previous-policies')
);
const NotificationsPage = lazy(() =>
  import('./components/dashboard/notifications').then((m) => ({
    default: m.NotificationsPage,
  }))
);
const Risk = lazy(() => import('./components/dashboard/risk'));
const Governance = lazy(() => import('./components/dashboard/governance'));

// --- Project Components ---
const Project = {
  Dashboard: ProjectDashboard,
  EntitySettings: lazy(() =>
    import('./components/project').then((m) => ({ default: m.EntitySettings }))
  ),
};
const AgentsDashboard = lazy(() =>
  import('./components/project/dashboard/agents-dashboard').then((m) => ({
    default: m.AgentsDashboard,
  }))
);
const DatasetsDashboard = lazy(() =>
  import('./components/project/dashboard/datasets-dashboard').then((m) => ({
    default: m.DatasetsDashboard,
  }))
);
const FunctionalModesDashboard = lazy(() =>
  import('./components/project/dashboard/functional-models-dashboard').then(
    (m) => ({ default: m.FunctionalModesDashboard })
  )
);
const ModelsDashboard = lazy(() =>
  import('./components/project/dashboard/models-dashboard').then((m) => ({
    default: m.ModelsDashboard,
  }))
);
const VendorModelsDashboard = lazy(() =>
  import('./components/project/dashboard/vender-models-dashboard').then(
    (m) => ({
      default: m.VendorModelsDashboard,
    })
  )
);
const VendorAgentsDashboard = lazy(() =>
  import('./components/project/dashboard/vendor-agents-dashboard').then(
    (m) => ({
      default: m.VendorAgentsDashboard,
    })
  )
);
const Description = lazy(() => import('./components/project/description'));
const RedirectToAiSystem = lazy(
  () => import('./components/project/redirect-to-ai-system')
);
const ProjectSettings = lazy(() => import('./components/project/settings'));
const AlertsPage = lazy(() => import('./components/project/recommendations'));
const ActionsPage = lazy(() => import('./components/project/actions'));
const GuardianAgentDetailPage = lazy(
  () => import('./components/dashboard/guardian/agent-detail-page')
);

const GuardianOrgAgentsPage = lazy(
  () => import('./components/dashboard/guardian/org-agents-page')
);

const GuardianOrgCoveragePage = lazy(
  () => import('./components/dashboard/guardian/org-coverage-page')
);

const MonitoringDetailPage = lazy(
  () =>
    import('./components/project/dashboard/monitoring/monitoring-detail-page')
);
const DashboardPolicyDetail = lazy(
  () => import('./components/project/policy-details')
);
const DocumentHub = lazy(() =>
  import('./components/documents/document-hub').then((m) => ({
    default: m.DocumentHub,
  }))
);

// --- Project Reports ---
const AiProjectReport = lazy(
  () => import('./components/project-reports/ai-project-report')
);
const ProjectAdvancedReports = lazy(
  () => import('./components/project/project-advanced-reports')
);
const ProjectRiskAssessmentReport = lazy(
  () => import('./components/project/project-risk-assessment-report')
);
const ProjectActionReport = lazy(
  () => import('./components/project/project-action-report')
);
const ProjectRiskReductionReport = lazy(
  () => import('./components/project/project-risk-reduction-report')
);
const ProjectEuAiDeepDiveReport = lazy(
  () => import('./components/project/project-eu-ai-deep-dive-report')
);
const FrameworkAnalysis = lazy(
  () => import('./components/project-reports/framework/framework-analysis')
);
const DataLineageMap = lazy(
  () => import('./components/project-reports/data-lineage-map/data-lineage-map')
);
const TemplateReportViewer = lazy(
  () => import('./components/project-reports/template-report-viewer')
);

// --- Report Generator ---
const Reports = lazy(() => import('./components/report-generator/reports'));
const AllProjectReport = lazy(
  () => import('./components/report-generator/all-project-report')
);
const ProjectReport = lazy(
  () => import('./components/report-generator/project-report')
);

// --- Settings Components ---
const ConfigurationManagement = lazy(
  () => import('./components/settings/configuration-management')
);
const PersonalSettingsDashboard = lazy(
  () => import('./components/settings/personal-settings-dashboard')
);

// --- Use Case Components ---
const Organisation = lazy(() => import('./components/use-case/organisation'));
const UseCaseActionReport = lazy(
  () => import('./components/use-case/use-case-action-report')
);
const UseCaseAdvancedReports = lazy(
  () => import('./components/use-case/use-case-advanced-reports')
);
const UseCaseBasicReports = lazy(
  () => import('./components/use-case/use-case-basic-reports')
);
const UseCaseDashboard = lazy(
  () => import('./components/use-case/use-case-dashboard')
);
const UseCasePerformanceReport = lazy(
  () => import('./components/use-case/use-case-performance-report')
);
const UseCaseRiskReductionReport = lazy(
  () => import('./components/use-case/use-case-risk-reduction-report')
);

// --- Other Components ---
const AddProjectPolicy = lazy(
  () => import('./components/add-project-policy/policies')
);
const FeatureAuth = lazy(() => import('./components/feature-auth'));
const BetaOnly = lazy(() => import('./components/beta-only'));
// ControlGen. The Epic/2504 generation form that used to sit here was removed
// 2026-08-17 (David: demo-only, "a magic look-at-this-we-made-a-control"). This
// section replaces it.
const ControlGen = lazy(() => import('./components/controlgen'));
const Members = lazy(() => import('./components/members'));
const ResourceWizardDialog = lazy(
  () => import('./components/model-wizard/index')
);
const PolicyGeneration = lazy(() => import('./components/policy-generation'));
const AsenionEmbeddedFrame = lazy(
  () => import('./components/sumo/asenion-integraion')
);
const UpgradeToUnlock = lazy(
  () => import('./components/upgrade-to-unlock/upgrade-to-unlock')
);
const NewProjectWizard = lazy(
  () => import('./components/project-wizard/new-project-wizard-dialog')
);
const AddAISystemWizard = lazy(
  () => import('./components/dashboard/add-ai-system/index')
);

// ============================================================================
// Helper to wrap lazy components with Suspense
// ============================================================================
const withSuspense = (Component: React.ReactNode) => (
  <Suspense fallback={<LoadingFallback />}>{Component}</Suspense>
);

/**
 * List of Route names
 * - The name defined here should not have `/`.
 * - nestedRoutes defines the list of routes that are allowed to be
 *   children of the `route`
 */
const dashboardRoutes = {
  route: 'dashboard',
  nestedRoutes: {
    USE_CASES: 'use-cases',
    ORGANISATION: 'organisation',
    COMPLIANCE: 'compliance',
    GUARDIAN_COVERAGE: 'guardian-coverage',
    GUARDIAN_AGENTS: 'guardian-agents',
    // NOT `:agentId` — that param is already taken by candidate-agent/:agentId,
    // and Landing folds any `agentId` into `effectiveId`, which it resolves as a
    // PROJECT id. Reusing the name made every guardian agent page fire a
    // "Cast to ObjectId failed ... for model Project" against the composite.
    GUARDIAN_AGENT_DETAIL: 'guardian-agents/:guardianAgentId',
    PROJECTS: 'projects',
    ORGANIZATION: 'organization',
    TASKS: 'tasks',
    STATUS: 'status',
    NOTIFICATIONS: 'notifications',
    RISK: 'risk',
    POLICY_LIBRARY: 'policy-library',
    ASSURANCE_LIBRARY: 'assurance-library',
    ASSURANCE: 'assurance',
    CREATE_AI_SYSTEM: 'create-ai-system',
    REPORTS: 'reports',
    TEST_GEN: 'test-gen',
    CONTROLGEN: 'controlgen',
    POLICY_GEN: 'policy-gen',
    RESPONSIBLE_AI_TRAINING: 'responsible-ai-training',
    VENDOR_MODEL: 'vendor-model',
    VENDOR_AGENT: 'vendor-agent',
  },
} as const;

const projectRoutes = {
  route: 'project/:projectId',
  nestedRoutes: {
    DASHBOARD: 'dashboard',
    DESCRIPTION: 'description',
    POLICY_DETAILS: 'policy-details',
    ASSESSMENTS: 'assessments',
    ASSESS_PROJECT: 'assess-project',
    REPORT: 'report',
    ASENION_CHATBOT: 'asenion-chatbot',
  },
} as const;

const projectMembersRoutes = {
  route: 'project-members/:projectId',
  nestedRoutes: {
    MEMBERS: 'members',
  },
} as const;

const orgAdminRoutes = {
  route: 'org-admin/:organizationId',
  nestedRoutes: {
    DASHBOARD: 'dashboard',
  },
} as const;

const orgAdminDashboardRoutes = {
  route: 'org-admin',
  nestedRoutes: {
    DASHBOARD: 'dashboard',
  },
} as const;

const systemAdminRoutes = {
  route: 'system-admin',
  nestedRoutes: {
    DASHBOARD: 'dashboard',
  },
} as const;

const aiSystemRoutes = {
  route: 'ai-system/:projectId',
  nestedRoutes: {
    DASHBOARD: 'dashboard',
    SETTINGS: 'settings',
    DOCUMENTS: 'documents',
    MODELS_LIST: 'models-list',
    FUNCTIONAL_MODELS_LIST: 'ai-usecases-list',
    CREATE_MODEL: 'create-model',
    CREATE_FUNCTIONAL_MODEL: 'create-ai-usecase',
    CREATE_DATASET: 'create-dataset',
    ASSESSMENT_DETAILS: 'assessment-details',
    PROJECT_MEMBERS: 'project-members',
    ADD_PROJECT_POLICY: 'add-policy',
    ASSESS_PROJECT: 'assess-project',
    REPORT: 'report',
    AI_SYSTEM_REPORT: 'ai-system-report',
    FRAMEWORK_ANALYSIS: 'framework-analysis',
    DATA_LINEAGE_MAP: 'data-lineage-map',
    TEMPLATE_REPORT_VIEW: 'template-report/:templateId',
    RECOMMENDATIONS: 'recommendations',
    ADVANCED_REPORTS: 'advanced-reports',
    RISK_ASSESSMENT_REPORT: 'risk-assessment-report',
    ACTION_REPORT: 'action-report',
    RISK_REDUCTION_REPORT: 'risk-reduction-report',
    EU_AI_DEEP_DIVE_REPORT: 'eu-ai-deep-dive-report',
    ALERTS: 'alerts',
    ACTIONS: 'actions',
    MONITORING_DETAIL: 'monitoring/:assessmentId',
  },
} as const;

const useCaseRoutes = {
  route: 'use-case/:useCaseId',
  nestedRoutes: {
    DASHBOARD: 'dashboard',
    BASIC_REPORT: 'basic-report',
    ADVANCED_REPORT: 'advanced-report',
    RISK_ASSESSMENT_REPORT: 'risk-assessment-report',
    ACTION_REPORT: 'action-report',
    RISK_REDUCTION_REPORT: 'risk-reduction-report',
  },
} as const;

const assessmentRoutes = {
  route: 'assessment/:assessmentId',
  nestedRoutes: {
    LANDING: '',
    GETTING_STARTED: 'getting-started',
    SUBMIT_FOR_REVIEW: 'submit-for-review',
    INVITE_TEAM: 'invite-team',
  },
} as const;
const functionalModelsRoutes = {
  route: 'ai-usecase/:functionalModelId',
  nestedRoutes: {
    CANDIDATES: 'candidates',
    CREATE_CANDIDATE: 'create-candidate',
    AGENTS: 'agents',
    CREATE_AGENT: 'create-agent',
    DATASETS: 'datasets',
    CREATE_DATASET: 'create-dataset',
    ASSESSMENT_DETAILS: 'assessment-details',
    DASHBOARD: 'dashboard',
    ADD_PROJECT_POLICY: 'add-policy',
    ASSESS_PROJECT: 'assess-project',
    REPORT: 'report',
    FUNCTIONAL_MODEL_REPORT: 'ai-usecase-report',
    TEMPLATE_REPORT_VIEW: 'template-report/:templateId',
    SETTINGS: 'settings',
    DOCUMENTS: 'documents',
    MONITORING_DETAIL: 'monitoring/:assessmentId',
  },
};

const vendorModelDetailsRoute = {
  route: 'vendor-model/:vendorModelId',
  nestedRoutes: {
    ASSESSMENT_DETAILS: 'assessment-details',
    REPORT: 'report',
  },
} as const;

const vendorAgentDetailsRoute = {
  route: 'vendor-agent/:vendorAgentId',
  nestedRoutes: {
    ASSESSMENT_DETAILS: 'assessment-details',
    REPORT: 'report',
  },
} as const;

const candidateModelDetailsRoute = {
  route: 'candidate-model/:candidateId',
  nestedRoutes: {
    CANDIDATE_DETAILS: 'candidate-details',
    DATASET_DETAILS: 'dataset-details',
    ASSESSMENT_DETAILS: 'assessment-details',
    ADD_PROJECT_POLICY: 'add-policy',
    ASSESS_PROJECT: 'assess-project',
    REPORT: 'report',
    PROJECT_REPORT: 'project-report',
    CANDIDATE_MODEL_REPORT: 'candidate-model-report',
    TEMPLATE_REPORT_VIEW: 'template-report/:templateId',
    SETTINGS: 'settings',
  },
};

const candidateAgentDetailsRoute = {
  route: 'candidate-agent/:agentId',
  nestedRoutes: {
    AGENT_DETAILS: 'agent-details',
    DATASET_DETAILS: 'dataset-details',
    ASSESSMENT_DETAILS: 'assessment-details',
    ADD_PROJECT_POLICY: 'add-policy',
    ASSESS_PROJECT: 'assess-project',
    REPORT: 'report',
    PROJECT_REPORT: 'project-report',
    SETTINGS: 'settings',
  },
};

const assessmentBundleRoutes = {
  route: 'bundle/:bundleId',
  nestedRoutes: {
    BUNDLE_FORM: 'form',
    RISK_OVER_TIME: 'risk-over-time',
  },
} as const;

const assessmentBundleVersionRoutes = {
  route: 'answer/:versionId',
  nestedRoutes: {
    VERSIONS_IDS: 'version',
  },
} as const;

const aiSystemPolicyRoutes = {
  route: 'add-policy',
  nestedRoutes: {},
} as const;

const functionalModelsPolicyRoutes = {
  route: 'add-policy',
  nestedRoutes: {},
} as const;

const projectReportRoutes = {
  route: 'report',
  nestedRoutes: {
    EXECUTIVE_REPORT: 'executive-report',
    PROJECT_REPORT: 'project-report',
  },
} as const;

const assessmentReportRoutes = {
  route: 'report',
  nestedRoutes: {
    ASSESSMENT_REPORT: 'assessment-report',
  },
} as const;

const policyDetailsRoutes = {
  route: 'governance/:policyId',
  nestedRoutes: {
    POLICY_DETAILS: 'policy-details',
    CONTROL_BUNDLES: 'control-bundles',
    EXCLUDED_PROJECT: 'excluded-project',
    PREVIOUS_VERSIONS: 'prev-version',
  },
} as const;

const previousControlDetailsRoutes = {
  route: 'control-bundles/:versionId',
  nestedRoutes: {
    CONTROL_BUNDLES: 'versions',
  },
} as const;

const policyGovernanceRoutes = {
  route: 'policy-library',
  nestedRoutes: {},
} as const;

const assuranceLibraryRoutes = {
  route: 'assurance-library',
  nestedRoutes: {},
} as const;

const adminRoutes = {
  route: 'admin',
  nestedRoutes: {
    DASHBOARD: 'dashboard',
    SYSTEM_ADMIN: 'system-admin',
    ORG_ADMIN: 'org-admin',
    REPORT_TEMPLATE_INIT: 'report-template-init',
  },
} as const;

const settingRoutes = {
  route: 'settings',
  nestedRoutes: {
    PERSONAL: 'personal',
    SYSTEM_ADMIN: 'system-admin',
  },
} as const;

const candidateModelPolicyRoutes = {
  route: 'add-policy',
  nestedRoutes: {},
} as const;

// All dashboard routes should be protected
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AuthRoute children={<Landing />} />,
    children: [
      {
        path: dashboardRoutes.route,
        children: [
          {
            path: dashboardRoutes.nestedRoutes.GUARDIAN_AGENT_DETAIL,
            element: withSuspense(
              <RequireCapability
                capability="org:view_governance"
                redirectTo="/dashboard/tasks"
              >
                <BetaOnly>
                  <GuardianAgentDetailPage />
                </BetaOnly>
              </RequireCapability>
            ),
          },
          {
            path: dashboardRoutes.nestedRoutes.GUARDIAN_COVERAGE,
            element: withSuspense(
              <RequireCapability
                capability="org:view_governance"
                redirectTo="/dashboard/tasks"
              >
                <BetaOnly>
                  <GuardianOrgCoveragePage />
                </BetaOnly>
              </RequireCapability>
            ),
          },
          {
            path: dashboardRoutes.nestedRoutes.GUARDIAN_AGENTS,
            element: withSuspense(
              <RequireCapability
                capability="org:view_governance"
                redirectTo="/dashboard/tasks"
              >
                <BetaOnly>
                  <GuardianOrgAgentsPage />
                </BetaOnly>
              </RequireCapability>
            ),
          },
          {
            path: dashboardRoutes.nestedRoutes.COMPLIANCE,
            element: withSuspense(
              <RequireCapability
                capability="org:create_project"
                redirectTo="/dashboard/tasks"
              >
                <DashboardCompliance />
              </RequireCapability>
            ),
          },
          {
            path: dashboardRoutes.nestedRoutes.PROJECTS,
            element: withSuspense(<ComplianceProjects />),
          },
          {
            path: dashboardRoutes.nestedRoutes.ORGANIZATION,
            element: withSuspense(<DashboardOrganization />),
          },
          {
            path: dashboardRoutes.nestedRoutes.TASKS,
            element: withSuspense(<DashboardTasks />),
          },
          {
            path: dashboardRoutes.nestedRoutes.STATUS,
            element: withSuspense(<DashboardStatus />),
          },
          {
            path: dashboardRoutes.nestedRoutes.NOTIFICATIONS,
            element: withSuspense(<NotificationsPage />),
          },
          {
            path: dashboardRoutes.nestedRoutes.RISK,
            element: withSuspense(<Risk />),
          },
          {
            path: dashboardRoutes.nestedRoutes.POLICY_LIBRARY,
            element: withSuspense(<Governance />),
          },
          {
            path: dashboardRoutes.nestedRoutes.ASSURANCE_LIBRARY,
            element: withSuspense(<AssuranceLibrary />),
          },
          {
            path: dashboardRoutes.nestedRoutes.ASSURANCE,
            element: withSuspense(<AssuranceHub />),
          },
          {
            path: dashboardRoutes.nestedRoutes.VENDOR_MODEL,
            element: withSuspense(<VendorModelsDashboard />),
          },
          {
            path: dashboardRoutes.nestedRoutes.VENDOR_AGENT,
            element: withSuspense(<VendorAgentsDashboard />),
          },
          {
            path: dashboardRoutes.nestedRoutes.TEST_GEN,
            element: withSuspense(
              <CapabilityAuth capability="platform:manage_system_admins">
                <AsenionGenAI />
              </CapabilityAuth>
            ),
          },
          {
            // Gated on platform:manage_system_admins for now, matching its AI Lab
            // neighbours. STRICTER than the intended gate (the beta flag), so it
            // cannot over-expose while entitlement is unsettled — swap for
            // org:view_controlgen when that capability exists.
            path: dashboardRoutes.nestedRoutes.CONTROLGEN,
            element: withSuspense(
              <CapabilityAuth capability="platform:manage_system_admins">
                <BetaOnly>
                  <ControlGen />
                </BetaOnly>
              </CapabilityAuth>
            ),
          },
          {
            path: dashboardRoutes.nestedRoutes.POLICY_GEN,
            element: withSuspense(
              <CapabilityAuth capability="platform:manage_system_admins">
                <PolicyGeneration />
              </CapabilityAuth>
            ),
          },
          {
            path: dashboardRoutes.nestedRoutes.CREATE_AI_SYSTEM,
            element: withSuspense(
              <RequireCapability capability="org:create_project">
                <AddAISystemWizard />
              </RequireCapability>
            ),
          },
          {
            path: dashboardRoutes.nestedRoutes.REPORTS,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <AllProjectReport />
              </RequireCapability>
            ),
          },
          {
            path: dashboardRoutes.nestedRoutes.RESPONSIBLE_AI_TRAINING,
            element: withSuspense(<ResponsibleAITraining />),
          },
          {
            path: dashboardRoutes.nestedRoutes.ORGANISATION,
            element: withSuspense(<Organisation />),
          },
        ],
      },
      {
        path: useCaseRoutes.route,
        children: [
          {
            path: useCaseRoutes.nestedRoutes.DASHBOARD,
            element: withSuspense(<UseCaseDashboard />),
          },
          {
            path: useCaseRoutes.nestedRoutes.BASIC_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <UseCaseBasicReports />
              </RequireCapability>
            ),
          },
          {
            path: useCaseRoutes.nestedRoutes.ADVANCED_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <UseCaseAdvancedReports />
              </RequireCapability>
            ),
          },
          {
            path: useCaseRoutes.nestedRoutes.RISK_ASSESSMENT_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <UseCasePerformanceReport />
              </RequireCapability>
            ),
          },
          {
            path: useCaseRoutes.nestedRoutes.ACTION_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <UseCaseActionReport />
              </RequireCapability>
            ),
          },
          {
            path: useCaseRoutes.nestedRoutes.RISK_REDUCTION_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <UseCaseRiskReductionReport />
              </RequireCapability>
            ),
          },
        ],
      },
      {
        path: projectRoutes.route,
        children: [
          {
            path: projectRoutes.nestedRoutes.DASHBOARD,
            element: withSuspense(<RedirectToAiSystem />),
          },
          {
            path: projectRoutes.nestedRoutes.DESCRIPTION,
            element: withSuspense(<Description />),
          },
          {
            path: projectRoutes.nestedRoutes.POLICY_DETAILS,
            element: withSuspense(<DashboardPolicyDetail />),
          },
          {
            path: projectRoutes.nestedRoutes.ASSESSMENTS,
            element: withSuspense(<RedirectToAiSystem />),
          },
          {
            path: projectRoutes.nestedRoutes.ASSESS_PROJECT,
            element: withSuspense(<Assessment.AssessProject />),
          },
          {
            path: projectRoutes.nestedRoutes.REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectReport />
              </RequireCapability>
            ),
          },
          {
            path: projectReportRoutes.route,
            children: [
              {
                path: projectReportRoutes.nestedRoutes.EXECUTIVE_REPORT,
                element: withSuspense(
                  <RequireCapability capability="report:view">
                    <Reports reportType="EXECUTIVE_REPORT" />
                  </RequireCapability>
                ),
              },
              {
                path: projectReportRoutes.nestedRoutes.PROJECT_REPORT,
                element: withSuspense(
                  <RequireCapability capability="report:view">
                    <Reports reportType="PROJECT_REPORT" />
                  </RequireCapability>
                ),
              },
            ],
          },
          {
            path: projectRoutes.nestedRoutes.ASENION_CHATBOT,
            element: withSuspense(
              <CapabilityAuth capability="platform:manage_system_admins">
                <AsenionEmbeddedFrame />
              </CapabilityAuth>
            ),
          },
        ],
      },
      {
        path: policyDetailsRoutes.route,
        children: [
          {
            path: policyDetailsRoutes.nestedRoutes.POLICY_DETAILS,
            element: withSuspense(<PolicyDetails />),
          },
          {
            path: policyDetailsRoutes.nestedRoutes.CONTROL_BUNDLES,
            element: withSuspense(<ControlBundle />),
          },
          {
            path: policyDetailsRoutes.nestedRoutes.EXCLUDED_PROJECT,
            element: withSuspense(<PolicyDetails showExcludedProject />),
          },
          {
            path: policyDetailsRoutes.nestedRoutes.PREVIOUS_VERSIONS,
            element: withSuspense(<PreviousPolicies />),
          },
          {
            path: previousControlDetailsRoutes.route,
            children: [
              {
                path: previousControlDetailsRoutes.nestedRoutes.CONTROL_BUNDLES,
                element: withSuspense(<ControlBundle isVersion />),
              },
            ],
          },
        ],
      },
      {
        path: adminRoutes.route,
        children: [
          {
            path: adminRoutes.nestedRoutes.SYSTEM_ADMIN,
            element: withSuspense(
              <RequireCapability capability="platform:manage_system_admins">
                <SystemAdminDashboard />
              </RequireCapability>
            ),
          },
          {
            path: `${adminRoutes.nestedRoutes.ORG_ADMIN}/:organizationId?`,
            element: withSuspense(
              <RequireCapability capability="org:manage_members">
                <OrgAdminDashboard />
              </RequireCapability>
            ),
          },

          {
            path: adminRoutes.nestedRoutes.REPORT_TEMPLATE_INIT,
            element: withSuspense(
              <CapabilityAuth capability="platform:manage_system_admins">
                <ReportTemplateInit />
              </CapabilityAuth>
            ),
          },
        ],
      },
      {
        path: projectMembersRoutes.route,
        children: [
          {
            path: projectMembersRoutes.nestedRoutes.MEMBERS,
            element: withSuspense(<Members />),
          },
        ],
      },
      {
        path: aiSystemRoutes.route,
        children: [
          {
            path: aiSystemRoutes.nestedRoutes.DASHBOARD,
            element: <Project.Dashboard />,
          },
          {
            path: aiSystemRoutes.nestedRoutes.SETTINGS,
            element: withSuspense(<ProjectSettings />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.DOCUMENTS,
            element: withSuspense(<DocumentHub />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.ASSESS_PROJECT,
            element: withSuspense(<Assessment.AssessProject />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.FUNCTIONAL_MODELS_LIST,
            element: withSuspense(<FunctionalModesDashboard />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectReport />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.AI_SYSTEM_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <AiProjectReport />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.FRAMEWORK_ANALYSIS,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <FeatureAuth requireBeta allowRedirect={false}>
                  <FrameworkAnalysis />
                </FeatureAuth>
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.DATA_LINEAGE_MAP,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <FeatureAuth requireBeta allowRedirect={false}>
                  <DataLineageMap />
                </FeatureAuth>
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.TEMPLATE_REPORT_VIEW,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <TemplateReportViewer />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.RECOMMENDATIONS,
            element: <Navigate to="../alerts" replace />,
          },
          {
            path: aiSystemRoutes.nestedRoutes.ALERTS,
            element: withSuspense(<AlertsPage />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.ADVANCED_REPORTS,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectAdvancedReports />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.RISK_ASSESSMENT_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectRiskAssessmentReport />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.ACTION_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectActionReport />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.RISK_REDUCTION_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectRiskReductionReport />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.EU_AI_DEEP_DIVE_REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectEuAiDeepDiveReport />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.ACTIONS,
            element: withSuspense(<ActionsPage />),
          },
          {
            path: functionalModelsRoutes.route,
            children: [
              {
                path: functionalModelsRoutes.nestedRoutes.CANDIDATES,
                element: withSuspense(<ModelsDashboard />),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.AGENTS,
                element: withSuspense(<AgentsDashboard />),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.ASSESS_PROJECT,
                element: withSuspense(<Assessment.AssessProject />),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.ASSESSMENT_DETAILS,
                element: withSuspense(<DashboardPolicyDetail />),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.DATASETS,
                element: withSuspense(<DatasetsDashboard />),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.ADD_PROJECT_POLICY,
                element: withSuspense(
                  <RequireCapability capability="org:view_governance">
                    <AddProjectPolicy />
                  </RequireCapability>
                ),
              },
              {
                // A use case's monitoring policy, opened under the use case so the
                // page reads the use case's assessments — see governance-table.
                path: functionalModelsRoutes.nestedRoutes.MONITORING_DETAIL,
                element: withSuspense(
                  <BetaOnly>
                    <MonitoringDetailPage />
                  </BetaOnly>
                ),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.REPORT,
                element: withSuspense(
                  <RequireCapability capability="report:view">
                    <ProjectReport />
                  </RequireCapability>
                ),
              },
              {
                path: functionalModelsRoutes.nestedRoutes
                  .FUNCTIONAL_MODEL_REPORT,
                element: withSuspense(
                  <RequireCapability capability="report:view">
                    <AiProjectReport />
                  </RequireCapability>
                ),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.TEMPLATE_REPORT_VIEW,
                element: withSuspense(
                  <RequireCapability capability="report:view">
                    <TemplateReportViewer />
                  </RequireCapability>
                ),
              },
              {
                path: candidateModelDetailsRoute.route,
                children: [
                  {
                    path: candidateModelDetailsRoute.nestedRoutes
                      .CANDIDATE_DETAILS,
                    element: <Project.Dashboard />,
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes
                      .ASSESS_PROJECT,
                    element: withSuspense(<Assessment.AssessProject />),
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes
                      .DATASET_DETAILS,
                    element: <Project.Dashboard />,
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes
                      .ASSESSMENT_DETAILS,
                    element: withSuspense(<DashboardPolicyDetail />),
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes
                      .ADD_PROJECT_POLICY,
                    element: withSuspense(
                      <RequireCapability capability="org:view_governance">
                        <AddProjectPolicy />
                      </RequireCapability>
                    ),
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes.REPORT,
                    element: withSuspense(
                      <RequireCapability capability="report:view">
                        <ProjectReport />
                      </RequireCapability>
                    ),
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes
                      .CANDIDATE_MODEL_REPORT,
                    element: withSuspense(
                      <RequireCapability capability="report:view">
                        <AiProjectReport />
                      </RequireCapability>
                    ),
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes
                      .TEMPLATE_REPORT_VIEW,
                    element: withSuspense(
                      <RequireCapability capability="report:view">
                        <TemplateReportViewer />
                      </RequireCapability>
                    ),
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes
                      .PROJECT_REPORT,
                    element: withSuspense(
                      <RequireCapability capability="report:view">
                        <Reports reportType="PROJECT_REPORT" />
                      </RequireCapability>
                    ),
                  },
                  {
                    path: candidateModelDetailsRoute.nestedRoutes.SETTINGS,
                    element: withSuspense(<Project.EntitySettings />),
                  },
                  {
                    path: assessmentRoutes.route,
                    children: [
                      {
                        path: assessmentRoutes.nestedRoutes.LANDING,
                        element: withSuspense(<Assessment.Landing />),
                      },
                      {
                        path: assessmentRoutes.nestedRoutes.GETTING_STARTED,
                        element: withSuspense(<Assessment.GettingStarted />),
                      },
                      {
                        path: assessmentBundleRoutes.route,
                        children: [
                          {
                            path: assessmentBundleRoutes.nestedRoutes
                              .BUNDLE_FORM,
                            element: withSuspense(<Assessment.BundleForm />),
                          },
                          {
                            path: assessmentBundleRoutes.nestedRoutes
                              .RISK_OVER_TIME,
                            element: withSuspense(<Assessment.RiskOverTime />),
                          },
                          {
                            path: assessmentBundleVersionRoutes.route,
                            children: [
                              {
                                path: assessmentBundleVersionRoutes.nestedRoutes
                                  .VERSIONS_IDS,
                                element: withSuspense(
                                  <Assessment.BundleForm />
                                ),
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: assessmentRoutes.nestedRoutes.SUBMIT_FOR_REVIEW,
                        element: withSuspense(<Assessment.SubmitForReview />),
                      },
                      {
                        path: assessmentRoutes.nestedRoutes.INVITE_TEAM,
                        element: withSuspense(<UpgradeToUnlock />),
                      },
                      {
                        path: assessmentReportRoutes.route,
                        children: [
                          {
                            path: assessmentReportRoutes.nestedRoutes
                              .ASSESSMENT_REPORT,
                            element: withSuspense(
                              <Reports reportType="ASSESSMENT_REPORT" />
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: candidateAgentDetailsRoute.route,
                children: [
                  {
                    path: candidateAgentDetailsRoute.nestedRoutes.AGENT_DETAILS,
                    element: <Project.Dashboard />,
                  },
                  {
                    path: candidateAgentDetailsRoute.nestedRoutes
                      .ASSESS_PROJECT,
                    element: withSuspense(<Assessment.AssessProject />),
                  },
                  {
                    path: candidateAgentDetailsRoute.nestedRoutes
                      .DATASET_DETAILS,
                    element: <Project.Dashboard />,
                  },
                  {
                    path: candidateAgentDetailsRoute.nestedRoutes
                      .ASSESSMENT_DETAILS,
                    element: withSuspense(<DashboardPolicyDetail />),
                  },
                  {
                    path: candidateAgentDetailsRoute.nestedRoutes
                      .ADD_PROJECT_POLICY,
                    element: withSuspense(
                      <RequireCapability capability="org:view_governance">
                        <AddProjectPolicy />
                      </RequireCapability>
                    ),
                  },
                  {
                    path: candidateAgentDetailsRoute.nestedRoutes.REPORT,
                    element: withSuspense(
                      <RequireCapability capability="report:view">
                        <ProjectReport />
                      </RequireCapability>
                    ),
                  },
                  {
                    path: candidateAgentDetailsRoute.nestedRoutes
                      .PROJECT_REPORT,
                    element: withSuspense(
                      <RequireCapability capability="report:view">
                        <Reports reportType="PROJECT_REPORT" />
                      </RequireCapability>
                    ),
                  },
                  {
                    path: candidateAgentDetailsRoute.nestedRoutes.SETTINGS,
                    element: withSuspense(<Project.EntitySettings />),
                  },
                  {
                    path: assessmentRoutes.route,
                    children: [
                      {
                        path: assessmentRoutes.nestedRoutes.LANDING,
                        element: withSuspense(<Assessment.Landing />),
                      },
                      {
                        path: assessmentRoutes.nestedRoutes.GETTING_STARTED,
                        element: withSuspense(<Assessment.GettingStarted />),
                      },
                      {
                        path: assessmentBundleRoutes.route,
                        children: [
                          {
                            path: assessmentBundleRoutes.nestedRoutes
                              .BUNDLE_FORM,
                            element: withSuspense(<Assessment.BundleForm />),
                          },
                          {
                            path: assessmentBundleRoutes.nestedRoutes
                              .RISK_OVER_TIME,
                            element: withSuspense(<Assessment.RiskOverTime />),
                          },
                          {
                            path: assessmentBundleVersionRoutes.route,
                            children: [
                              {
                                path: assessmentBundleVersionRoutes.nestedRoutes
                                  .VERSIONS_IDS,
                                element: withSuspense(
                                  <Assessment.BundleForm />
                                ),
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: assessmentRoutes.nestedRoutes.SUBMIT_FOR_REVIEW,
                        element: withSuspense(<Assessment.SubmitForReview />),
                      },
                      {
                        path: assessmentRoutes.nestedRoutes.INVITE_TEAM,
                        element: withSuspense(<UpgradeToUnlock />),
                      },
                      {
                        path: assessmentReportRoutes.route,
                        children: [
                          {
                            path: assessmentReportRoutes.nestedRoutes
                              .ASSESSMENT_REPORT,
                            element: withSuspense(
                              <Reports reportType="ASSESSMENT_REPORT" />
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: functionalModelsRoutes.nestedRoutes.CREATE_CANDIDATE,
                element: withSuspense(
                  <ResourceWizardDialog open type="model" />
                ),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.CREATE_AGENT,
                element: withSuspense(
                  <ResourceWizardDialog open type="agent" />
                ),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.CREATE_DATASET,
                element: withSuspense(
                  <ResourceWizardDialog open type="dataset" />
                ),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.DASHBOARD,
                element: <Project.Dashboard />,
              },
              {
                path: functionalModelsRoutes.nestedRoutes.SETTINGS,
                element: withSuspense(<Project.EntitySettings />),
              },
              {
                path: functionalModelsRoutes.nestedRoutes.DOCUMENTS,
                element: withSuspense(<DocumentHub />),
              },
              {
                path: assessmentRoutes.route,
                children: [
                  {
                    path: assessmentRoutes.nestedRoutes.LANDING,
                    element: withSuspense(<Assessment.Landing />),
                  },
                  {
                    path: assessmentRoutes.nestedRoutes.GETTING_STARTED,
                    element: withSuspense(<Assessment.GettingStarted />),
                  },
                  {
                    path: assessmentBundleRoutes.route,
                    children: [
                      {
                        path: assessmentBundleRoutes.nestedRoutes.BUNDLE_FORM,
                        element: withSuspense(<Assessment.BundleForm />),
                      },
                      {
                        path: assessmentBundleRoutes.nestedRoutes
                          .RISK_OVER_TIME,
                        element: withSuspense(<Assessment.RiskOverTime />),
                      },
                      {
                        path: assessmentBundleVersionRoutes.route,
                        children: [
                          {
                            path: assessmentBundleVersionRoutes.nestedRoutes
                              .VERSIONS_IDS,
                            element: withSuspense(<Assessment.BundleForm />),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: assessmentRoutes.nestedRoutes.SUBMIT_FOR_REVIEW,
                    element: withSuspense(<Assessment.SubmitForReview />),
                  },
                  {
                    path: assessmentRoutes.nestedRoutes.INVITE_TEAM,
                    element: withSuspense(<UpgradeToUnlock />),
                  },
                  {
                    path: assessmentReportRoutes.route,
                    children: [
                      {
                        path: assessmentReportRoutes.nestedRoutes
                          .ASSESSMENT_REPORT,
                        element: withSuspense(
                          <Reports reportType="ASSESSMENT_REPORT" />
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: vendorModelDetailsRoute.route,
                children: [
                  {
                    path: vendorModelDetailsRoute.nestedRoutes
                      .ASSESSMENT_DETAILS,
                    element: withSuspense(<ModelCardAssessment />),
                  },
                ],
              },
            ],
          },
          {
            path: aiSystemRoutes.nestedRoutes.CREATE_FUNCTIONAL_MODEL,
            element: withSuspense(
              <ResourceWizardDialog open type="ai-usecase" />
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.CREATE_MODEL,
            element: withSuspense(<ResourceWizardDialog open type="model" />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.CREATE_DATASET,
            element: withSuspense(<ResourceWizardDialog open type="dataset" />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.ASSESSMENT_DETAILS,
            element: withSuspense(<DashboardPolicyDetail />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.PROJECT_MEMBERS,
            element: withSuspense(<Members />),
          },
          {
            path: aiSystemRoutes.nestedRoutes.ADD_PROJECT_POLICY,
            element: withSuspense(
              <RequireCapability capability="org:view_governance">
                <AddProjectPolicy />
              </RequireCapability>
            ),
          },
          {
            path: aiSystemRoutes.nestedRoutes.MONITORING_DETAIL,
            element: withSuspense(
              <BetaOnly>
                <MonitoringDetailPage />
              </BetaOnly>
            ),
          },
          {
            path: assessmentRoutes.route,
            children: [
              {
                path: assessmentRoutes.nestedRoutes.LANDING,
                element: withSuspense(<Assessment.Landing />),
              },
              {
                path: assessmentRoutes.nestedRoutes.GETTING_STARTED,
                element: withSuspense(<Assessment.GettingStarted />),
              },
              {
                path: assessmentBundleRoutes.route,
                children: [
                  {
                    path: assessmentBundleRoutes.nestedRoutes.BUNDLE_FORM,
                    element: withSuspense(<Assessment.BundleForm />),
                  },
                  {
                    path: assessmentBundleRoutes.nestedRoutes.RISK_OVER_TIME,
                    element: withSuspense(<Assessment.RiskOverTime />),
                  },
                  {
                    path: assessmentBundleVersionRoutes.route,
                    children: [
                      {
                        path: assessmentBundleVersionRoutes.nestedRoutes
                          .VERSIONS_IDS,
                        element: withSuspense(<Assessment.BundleForm />),
                      },
                    ],
                  },
                ],
              },
              {
                path: assessmentRoutes.nestedRoutes.SUBMIT_FOR_REVIEW,
                element: withSuspense(<Assessment.SubmitForReview />),
              },
              {
                path: assessmentRoutes.nestedRoutes.INVITE_TEAM,
                element: withSuspense(<UpgradeToUnlock />),
              },
              {
                path: assessmentReportRoutes.route,
                children: [
                  {
                    path: assessmentReportRoutes.nestedRoutes.ASSESSMENT_REPORT,
                    element: withSuspense(
                      <Reports reportType="ASSESSMENT_REPORT" />
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: vendorModelDetailsRoute.route,
        children: [
          {
            path: vendorModelDetailsRoute.nestedRoutes.ASSESSMENT_DETAILS,
            element: withSuspense(<ModelCardAssessment />),
          },
          {
            path: vendorModelDetailsRoute.nestedRoutes.REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectReport />
              </RequireCapability>
            ),
          },
          {
            path: assessmentRoutes.route,
            children: [
              {
                path: assessmentRoutes.nestedRoutes.LANDING,
                element: withSuspense(<Assessment.Landing />),
              },
              {
                path: assessmentRoutes.nestedRoutes.GETTING_STARTED,
                element: withSuspense(<Assessment.GettingStarted />),
              },
              {
                path: assessmentBundleRoutes.route,
                children: [
                  {
                    path: assessmentBundleRoutes.nestedRoutes.BUNDLE_FORM,
                    element: withSuspense(<Assessment.BundleForm />),
                  },
                  {
                    path: assessmentBundleRoutes.nestedRoutes.RISK_OVER_TIME,
                    element: withSuspense(<Assessment.RiskOverTime />),
                  },
                  {
                    path: assessmentBundleVersionRoutes.route,
                    children: [
                      {
                        path: assessmentBundleVersionRoutes.nestedRoutes
                          .VERSIONS_IDS,
                        element: withSuspense(<Assessment.BundleForm />),
                      },
                    ],
                  },
                ],
              },
              {
                path: assessmentRoutes.nestedRoutes.SUBMIT_FOR_REVIEW,
                element: withSuspense(<Assessment.SubmitForReview />),
              },
              {
                path: assessmentRoutes.nestedRoutes.INVITE_TEAM,
                element: withSuspense(<UpgradeToUnlock />),
              },
              {
                path: assessmentReportRoutes.route,
                children: [
                  {
                    path: assessmentReportRoutes.nestedRoutes.ASSESSMENT_REPORT,
                    element: withSuspense(
                      <Reports reportType="ASSESSMENT_REPORT" />
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: vendorAgentDetailsRoute.route,
        children: [
          {
            path: vendorAgentDetailsRoute.nestedRoutes.ASSESSMENT_DETAILS,
            element: withSuspense(<ModelCardAssessment />),
          },
          {
            path: vendorAgentDetailsRoute.nestedRoutes.REPORT,
            element: withSuspense(
              <RequireCapability capability="report:view">
                <ProjectReport />
              </RequireCapability>
            ),
          },
          {
            path: assessmentRoutes.route,
            children: [
              {
                path: assessmentRoutes.nestedRoutes.LANDING,
                element: withSuspense(<Assessment.Landing />),
              },
              {
                path: assessmentRoutes.nestedRoutes.GETTING_STARTED,
                element: withSuspense(<Assessment.GettingStarted />),
              },
              {
                path: assessmentBundleRoutes.route,
                children: [
                  {
                    path: assessmentBundleRoutes.nestedRoutes.BUNDLE_FORM,
                    element: withSuspense(<Assessment.BundleForm />),
                  },
                  {
                    path: assessmentBundleRoutes.nestedRoutes.RISK_OVER_TIME,
                    element: withSuspense(<Assessment.RiskOverTime />),
                  },
                  {
                    path: assessmentBundleVersionRoutes.route,
                    children: [
                      {
                        path: assessmentBundleVersionRoutes.nestedRoutes
                          .VERSIONS_IDS,
                        element: withSuspense(<Assessment.BundleForm />),
                      },
                    ],
                  },
                ],
              },
              {
                path: assessmentRoutes.nestedRoutes.SUBMIT_FOR_REVIEW,
                element: withSuspense(<Assessment.SubmitForReview />),
              },
              {
                path: assessmentRoutes.nestedRoutes.INVITE_TEAM,
                element: withSuspense(<UpgradeToUnlock />),
              },
              {
                path: assessmentReportRoutes.route,
                children: [
                  {
                    path: assessmentReportRoutes.nestedRoutes.ASSESSMENT_REPORT,
                    element: withSuspense(
                      <Reports reportType="ASSESSMENT_REPORT" />
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        // Where an assistant's "Sign in with Fairly" lands; the server-side endpoints live under the same prefix.
        path: 'guardian/oauth/authorize',
        element: withSuspense(<GuardianOAuthAuthorize />),
      },
      {
        path: settingRoutes.route,
        children: [
          {
            path: settingRoutes.nestedRoutes.PERSONAL,
            element: withSuspense(<PersonalSettingsDashboard />),
          },
          {
            path: settingRoutes.nestedRoutes.SYSTEM_ADMIN,
            element: withSuspense(
              <RequireCapability capability="platform:manage_system_admins">
                <SystemAdminDashboard />
              </RequireCapability>
            ),
          },
        ],
      },
      {
        path: orgAdminRoutes.route,
        children: [
          {
            path: orgAdminRoutes.nestedRoutes.DASHBOARD,
            element: withSuspense(<OrgAdminDashboard />),
          },
        ],
      },
      {
        path: orgAdminDashboardRoutes.route,
        children: [
          {
            path: orgAdminDashboardRoutes.nestedRoutes.DASHBOARD,
            element: withSuspense(<OrgAdminDashboard />),
          },
        ],
      },
      {
        path: systemAdminRoutes.route,
        children: [
          {
            path: systemAdminRoutes.nestedRoutes.DASHBOARD,
            element: withSuspense(
              <RequireCapability capability="platform:manage_system_admins">
                <SystemAdminDashboard />
              </RequireCapability>
            ),
          },
          {
            path: settingRoutes.nestedRoutes.SYSTEM_ADMIN,
            element: (
              <RequireCapability capability="platform:manage_system_admins">
                <SystemAdminDashboard />
              </RequireCapability>
            ),
          },
        ],
      },
      {
        path: orgAdminRoutes.route,
        children: [
          {
            path: orgAdminRoutes.nestedRoutes.DASHBOARD,
            element: <OrgAdminDashboard />,
          },
        ],
      },
      {
        path: orgAdminDashboardRoutes.route,
        children: [
          {
            path: orgAdminDashboardRoutes.nestedRoutes.DASHBOARD,
            element: <OrgAdminDashboard />,
          },
        ],
      },
      {
        path: systemAdminRoutes.route,
        children: [
          {
            path: systemAdminRoutes.nestedRoutes.DASHBOARD,
            element: (
              <RequireCapability capability="platform:manage_system_admins">
                <SystemAdminDashboard />
              </RequireCapability>
            ),
          },
        ],
      },
    ],
  },
];

interface NavigateToOpt extends NavigateOptions {
  params: Record<string, string>;
}

function routeUtilFactory<
  T extends {
    nestedRoutes: { [P in keyof T['nestedRoutes']]: T['nestedRoutes'][P] };
  },
>(route: Readonly<T>, routePrefix: string) {
  /**
   * Resolve nested route to the fully resolved path
   */
  const getFullPath = (routeKey: keyof T['nestedRoutes']) => {
    return `${routePrefix}/${route.nestedRoutes[routeKey]}`;
  };

  /**
   * Helper hook for navigating routes
   */
  const useHelper = () => {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    /**
     * Navigate to the given nested route
     */
    const navigateTo = (
      routeKey: keyof T['nestedRoutes'],
      opt?: NavigateToOpt,
      queryParams?: string
    ) => {
      const fullPath = getFullPath(routeKey);
      const additionParams = opt?.params;
      const path = generatePath(fullPath, {
        ...params,
        ...additionParams,
      });
      if (queryParams?.length) {
        return navigate(`${path}${queryParams}`, opt);
      }
      navigate(path, opt);
    };

    /**
     * Check if current route matches the input nested route
     *
     * @param routes route definition object
     * @param routeKey nested route key
     * @example
     * // Current route is /dashboard/compliance
     * const match = matchRoute('COMPLIANCE');
     * expect(match).toBe(true);
     *
     * // Current route is /dashboard/risk
     * const match = matchRoute('RISK');
     * expect(match).toBe(false);
     */
    const matchRoute = React.useCallback(
      (
        routeKey: keyof T['nestedRoutes'],
        additionParams?: Record<string, string>
      ) => {
        try {
          const fullPath = getFullPath(routeKey);
          const path = generatePath(fullPath, {
            ...params,
            ...additionParams,
          });
          return path === location.pathname;
        } catch (err) {
          // some replacement variables are not fully resolved
          return false;
        }
      },
      [location]
    );

    return { navigateTo, matchRoute };
  };

  interface LinkProps extends Omit<RRLinkProps, 'to'> {
    className?: string;
    to: keyof T['nestedRoutes'];
    /**
     * Additional router parameters for resolving the nested routes
     */
    params?: Record<string, string>;
    /**
     * Color of text (default is blue)
     */
    color?: string;
  }

  /**
   * Anchor element that can navigate to nested routes
   */
  const LinkImpl = React.forwardRef<HTMLAnchorElement, LinkProps>(
    ({ className, to, params, color, ...props }, ref) => {
      const routeParams = useParams();
      const fullPath = getFullPath(to);
      const href = generatePath(fullPath, {
        ...routeParams,
        ...params,
      });
      return <RRLink ref={ref} className={className} to={href} {...props} />;
    }
  );
  LinkImpl.displayName = 'LinkImpl';
  /**
   * Customized React router Link component.
   *
   * It inherits all properties of a react router Link component.
   */
  const Link = muiStyled(LinkImpl)(({ theme, color = DesignToken.link }) => {
    return css`
      color: ${color};
      :hover {
        color: ${darken(color, 0.2)};
      }
    `;
  });

  return { useHelper, routePrefix, Link };
}

/**
 * Dashboard route helper utility
 * Manages routes for main dashboard views and operations
 * Base path: /dashboard
 * @example
 * // Navigate to compliance dashboard
 * dashboardRouteUtil.useHelper().navigateTo('COMPLIANCE');
 * // Navigate to risk dashboard
 * dashboardRouteUtil.useHelper().navigateTo('RISK');
 * // Navigate to governance
 * dashboardRouteUtil.useHelper().navigateTo('GOVERNANCE');
 */
export const dashboardRouteUtil = routeUtilFactory(
  dashboardRoutes,
  `/${dashboardRoutes.route}`
);

/**
 * AI System Dashboard route helper utility
 * Handles routes for AI system management and overview
 * Base path: /ai-system/:projectId
 * @example
 * // Navigate to AI system dashboard
 * aiDashboardUtils.useHelper().navigateTo('DASHBOARD', {
 *   params: { projectId: '123' }
 * });
 * // Navigate to models list
 * aiDashboardUtils.useHelper().navigateTo('MODELS_LIST', {
 *   params: { projectId: '123' }
 * });
 */
export const aiDashboardUtils = routeUtilFactory(
  aiSystemRoutes,
  `/${aiSystemRoutes.route}`
);

/**
 * Runtime Monitoring route helper utility.
 * Base path: /ai-system/:projectId/monitoring/:assessmentId
 *
 * EXISTS FOR THE LEFT NAV, not for navigation. Monitoring takes over the global
 * sidebar the way an assessment does, and `LeftNavMenu` selects on an exact
 * route-prefix string — so the takeover needs a prefix of its own that is
 * MORE SPECIFIC than `aiDashboardUtils` (`/ai-system/:projectId`), which
 * FunctionalModelsLeftNav already claims. react-router's `matchRoutes` ranks by
 * specificity and the literal `monitoring` segment outranks the bare
 * `:projectId` branch, so the monitoring nav wins on monitoring routes and the
 * project nav keeps every other AI-system route.
 *
 * `nestedRoutes` is empty on purpose: this route has no children, and inventing
 * some to satisfy the factory would put paths in the router that nothing serves.
 */
export const aiSystemMonitoringRouteUtil = routeUtilFactory(
  { nestedRoutes: {} },
  `/${aiSystemRoutes.route}/${aiSystemRoutes.nestedRoutes.MONITORING_DETAIL}`
);

/**
 * The same takeover for a monitoring policy on a USE CASE:
 * /ai-system/:projectId/ai-usecase/:functionalModelId/monitoring/:assessmentId.
 * More specific than `functionalModelsRouteUtil`, which keeps every other use-case
 * route, for the reason given above. The monitoring nav answers to either prefix.
 */
export const functionalModelMonitoringRouteUtil = routeUtilFactory(
  { nestedRoutes: {} },
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${functionalModelsRoutes.nestedRoutes.MONITORING_DETAIL}`
);

/**
 * Use Case route helper utility
 * Handles routes for use case dashboard and reports (Anch integration)
 * Base path: /use-case/:useCaseId
 * @example
 * // Navigate to use case dashboard
 * useCaseRouteUtil.useHelper().navigateTo('DASHBOARD', {
 *   params: { useCaseId: '587' }
 * });
 * // Navigate to advanced reports
 * useCaseRouteUtil.useHelper().navigateTo('ADVANCED_REPORT', {
 *   params: { useCaseId: '587' }
 * });
 */
export const useCaseRouteUtil = routeUtilFactory(
  useCaseRoutes,
  `/${useCaseRoutes.route}`
);

export const orgAdminDetailsUtils = routeUtilFactory(
  orgAdminRoutes,
  `/${orgAdminRoutes.route}`
);

export const systemAdminDashboardUtils = routeUtilFactory(
  systemAdminRoutes,
  `/${systemAdminRoutes.route}`
);

export const orgAdminDashboardUtils = routeUtilFactory(
  orgAdminDashboardRoutes,
  `/${orgAdminDashboardRoutes.route}`
);

export const vendorModelDashboardUtils = routeUtilFactory(
  vendorModelDetailsRoute,
  `/${vendorModelDetailsRoute.route}`
);

export const vendorAgentDashboardUtils = routeUtilFactory(
  vendorAgentDetailsRoute,
  `/${vendorAgentDetailsRoute.route}`
);

/**
 * Functional Model Dashboard route helper utility
 * Manages routes for functional model operations and views
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId
 * @example
 * // Navigate to candidates view
 * functionalModelDashboardUtils.useHelper().navigateTo('CANDIDATES', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456'
 *   }
 * });
 * // Navigate to datasets view
 * functionalModelDashboardUtils.useHelper().navigateTo('DATASETS', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456'
 *   }
 * });
 */
export const functionalModelDashboardUtils = routeUtilFactory(
  functionalModelsRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}`
);

/**
 * Candidate Model Dashboard route helper utility
 * Handles routes for specific candidate model operations
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/candidate-model/:candidateId
 * @example
 * // Navigate to candidate details
 * candidateModelDashboardUtils.useHelper().navigateTo('CANDIDATE_DETAILS', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     candidateId: '789'
 *   }
 * });
 */
export const candidateModelDashboardUtils = routeUtilFactory(
  candidateModelDetailsRoute,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateModelDetailsRoute.route}`
);

export const vendorModelDetailsRouteUtil = routeUtilFactory(
  vendorModelDetailsRoute,
  `/${dashboardRoutes.route}/${vendorModelDetailsRoute.route}`
);

/**
 * Project route helper utility
 * Manages routes for project-level operations
 * Base path: /project/:projectId
 * @example
 * // Navigate to project dashboard
 * projectRouteUtil.useHelper().navigateTo('DASHBOARD', {
 *   params: { projectId: '123' }
 * });
 * // Navigate to project description
 * projectRouteUtil.useHelper().navigateTo('DESCRIPTION', {
 *   params: { projectId: '123' }
 * });
 */
export const projectRouteUtil = routeUtilFactory(
  projectRoutes,
  `/${projectRoutes.route}`
);

/**
 * Project Members route helper utility
 * Handles routes for project member management
 * Base path: /project-members/:projectId
 * @example
 * // Navigate to members list
 * projectMembersRouteUtil.useHelper().navigateTo('MEMBERS', {
 *   params: { projectId: '123' }
 * });
 */
export const projectMembersRouteUtil = routeUtilFactory(
  projectMembersRoutes,
  `/${projectMembersRoutes.route}`
);

/**
 * Assessment route helper utility
 * Manages routes for project assessments
 * Base path: /project/:projectId/assessment/:assessmentId
 * @example
 * // Navigate to assessment landing
 * assessmentRouteUtil.useHelper().navigateTo('LANDING', {
 *   params: {
 *     projectId: '123',
 *     assessmentId: '456'
 *   }
 * });
 * // Navigate to getting started
 * assessmentRouteUtil.useHelper().navigateTo('GETTING_STARTED', {
 *   params: {
 *     projectId: '123',
 *     assessmentId: '456'
 *   }
 * });
 */
export const assessmentRouteUtil = routeUtilFactory(
  assessmentRoutes,
  `/${projectRoutes.route}/${assessmentRoutes.route}`
);

/**
 * AI System Assessment route helper utility
 * Handles assessment routes for AI systems
 * Base path: /ai-system/:projectId/assessment/:assessmentId
 * @example
 * // Navigate to assessment landing
 * aiSystemAssessmentRouteUtil.useHelper().navigateTo('LANDING', {
 *   params: {
 *     projectId: '123',
 *     assessmentId: '456'
 *   }
 * });
 */
export const aiSystemAssessmentRouteUtil = routeUtilFactory(
  assessmentRoutes,
  `/${aiSystemRoutes.route}/${assessmentRoutes.route}`
);

export const vendorModelAssessmentRouteUtil = routeUtilFactory(
  assessmentRoutes,
  `/${vendorModelDetailsRoute.route}/${assessmentRoutes.route}`
);

export const vendorAgentAssessmentRouteUtil = routeUtilFactory(
  assessmentRoutes,
  `/${vendorAgentDetailsRoute.route}/${assessmentRoutes.route}`
);

/**
 * Functional Model Assessment route helper utility
 * Manages assessment routes for functional models
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/assessment/:assessmentId
 * @example
 * // Navigate to assessment landing
 * functionalModelAssessmentRouteUtil.useHelper().navigateTo('LANDING', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     assessmentId: '789'
 *   }
 * });
 */
export const functionalModelAssessmentRouteUtil = routeUtilFactory(
  assessmentRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${assessmentRoutes.route}`
);

/**
 * Candidate Model Assessment route helper utility
 * Handles assessment routes for candidate models
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/candidate-model/:candidateId/assessment/:assessmentId
 * @example
 * // Navigate to assessment landing
 * candidateModelAssessmentRouteUtil.useHelper().navigateTo('LANDING', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     candidateId: '789',
 *     assessmentId: 'a1'
 *   }
 * });
 */
export const candidateModelAssessmentRouteUtil = routeUtilFactory(
  assessmentRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateModelDetailsRoute.route}/${assessmentRoutes.route}`
);

export const candidateAgentAssessmentRouteUtil = routeUtilFactory(
  assessmentRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateAgentDetailsRoute.route}/${assessmentRoutes.route}`
);

/**
 * AI System Policy route helper utility
 * Manages routes for AI system policy operations
 * Base path: /ai-system/:projectId/add-policy
 * @example
 * // Navigate to add policy view
 * aiSystemPolicyRouteUtil.useHelper().navigateTo('ADD_POLICY', {
 *   params: {
 *     projectId: '123'
 *   }
 * });
 */
export const aiSystemPolicyRouteUtil = routeUtilFactory(
  aiSystemPolicyRoutes,
  `/${aiSystemRoutes.route}/${aiSystemPolicyRoutes.route}`
);

/**
 * Functional Models Policy route helper utility
 * Handles routes for functional model policy operations
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/add-policy
 * @example
 * // Navigate to add policy for functional model
 * functionalModelsPolicyRouteUtil.useHelper().navigateTo('ADD_POLICY', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456'
 *   }
 * });
 */
export const functionalModelsPolicyRouteUtil = routeUtilFactory(
  functionalModelsPolicyRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${functionalModelsPolicyRoutes.route}`
);

/**
 * Project Report route helper utility
 * Manages routes for project-level reports
 * Base path: /project/:projectId/report
 * @example
 * // Navigate to executive report
 * projectReportRouteUtil.useHelper().navigateTo('EXECUTIVE_REPORT', {
 *   params: {
 *     projectId: '123'
 *   }
 * });
 * // Navigate to project report
 * projectReportRouteUtil.useHelper().navigateTo('PROJECT_REPORT', {
 *   params: {
 *     projectId: '123'
 *   }
 * });
 */
export const projectReportRouteUtil = routeUtilFactory(
  projectReportRoutes,
  `/${projectRoutes.route}/${projectReportRoutes.route}`
);

/**
 * Assessment Report route helper utility
 * Handles routes for assessment-level reports
 * Base path: /project/:projectId/assessment/:assessmentId/report
 * @example
 * // Navigate to assessment report
 * assessmentReportRouteUtil.useHelper().navigateTo('ASSESSMENT_REPORT', {
 *   params: {
 *     projectId: '123',
 *     assessmentId: '456'
 *   }
 * });
 */
export const assessmentReportRouteUtil = routeUtilFactory(
  assessmentReportRoutes,
  `/${projectRoutes.route}/${assessmentRoutes.route}/${assessmentReportRoutes.route}`
);

/**
 * Candidate Model Assessment Report route helper utility
 * Manages routes for candidate model assessment reports
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/candidate-model/:candidateId/assessment/:assessmentId/report
 * @example
 * // Navigate to candidate model assessment report
 * candidateModelAssessmentReportRouteUtil.useHelper().navigateTo('ASSESSMENT_REPORT', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     candidateId: '789',
 *     assessmentId: 'a1'
 *   }
 * });
 */
export const candidateModelAssessmentReportRouteUtil = routeUtilFactory(
  assessmentReportRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateModelDetailsRoute.route}/${assessmentRoutes.route}/${assessmentReportRoutes.route}`
);

/**
 * AI System Assessment Report route helper utility
 * Base path: /ai-system/:projectId/assessment/:assessmentId/report
 */
export const aiSystemAssessmentReportRouteUtil = routeUtilFactory(
  assessmentReportRoutes,
  `/${aiSystemRoutes.route}/${assessmentRoutes.route}/${assessmentReportRoutes.route}`
);

/**
 * Functional Model Assessment Report route helper utility
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/assessment/:assessmentId/report
 */
export const functionalModelAssessmentReportRouteUtil = routeUtilFactory(
  assessmentReportRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${assessmentRoutes.route}/${assessmentReportRoutes.route}`
);
/**
 * Policy Details route helper utility
 * Manages routes for policy detail views and operations
 * Base path: /governance/:policyId
 * @example
 * // Navigate to policy details
 * policyDetailsRoutesUtil.useHelper().navigateTo('POLICY_DETAILS', {
 *   params: {
 *     policyId: '123'
 *   }
 * });
 * // Navigate to control bundles
 * policyDetailsRoutesUtil.useHelper().navigateTo('CONTROL_BUNDLES', {
 *   params: {
 *     policyId: '123'
 *   }
 * });
 */
export const policyDetailsRoutesUtil = routeUtilFactory(
  policyDetailsRoutes,
  `/${policyDetailsRoutes.route}`
);

/**
 * Policy Version route helper utility
 * Handles routes for policy version management
 * Base path: /governance/:policyId/control-bundles/:versionId
 * @example
 * // Navigate to version control bundles
 * policyVersionsRoutesUtil.useHelper().navigateTo('CONTROL_BUNDLES', {
 *   params: {
 *     policyId: '123',
 *     versionId: 'v1'
 *   }
 * });
 */
export const policyVersionsRoutesUtil = routeUtilFactory(
  previousControlDetailsRoutes,
  `/${policyDetailsRoutes.route}/${previousControlDetailsRoutes.route}`
);

/**
 * Governance route helper utility
 * Manages routes for governance-related operations
 * Base path: /dashboard/governance
 * @example
 * // Navigate to governance dashboard
 * governanceRoutesUtil.useHelper().navigateTo('DASHBOARD');
 * // The route can be accessed using governanceRoutesUtil.routePrefix
 * // to get the base path: '/dashboard/governance'
 */
export const governanceRoutesUtil = routeUtilFactory(
  policyGovernanceRoutes,
  `/${dashboardRoutes.route}/${policyGovernanceRoutes.route}`
);
export const assuranceLibraryRoutesUtil = routeUtilFactory(
  policyGovernanceRoutes,
  `/${dashboardRoutes.route}/${assuranceLibraryRoutes.route}`
);
/**
 * assessment bundle route helper utility
 */
/**
 * Assessment Bundle route helper utility
 * Manages routes for project assessment bundles
 * Base path: /project/:projectId/assessment/:assessmentId/bundle/:bundleId
 * @example
 * // Navigate to bundle form
 * assessmentBundleRouteUtil.useHelper().navigateTo('BUNDLE_FORM', {
 *   params: {
 *     projectId: '123',
 *     assessmentId: '456',
 *     bundleId: '789'
 *   }
 * });
 */
export const assessmentBundleRouteUtil = routeUtilFactory(
  assessmentBundleRoutes,
  `/${projectRoutes.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}`
);

/**
 * AI System Bundle route helper utility
 * Handles routes for AI system assessment bundles
 * Base path: /ai-system/:projectId/assessment/:assessmentId/bundle/:bundleId
 * @example
 * // Navigate to risk over time view
 * aiSystemBundleRouteUtil.useHelper().navigateTo('RISK_OVER_TIME', {
 *   params: {
 *     projectId: '123',
 *     assessmentId: '456',
 *     bundleId: '789'
 *   }
 * });
 */
export const aiSystemBundleRouteUtil = routeUtilFactory(
  assessmentBundleRoutes,
  `/${aiSystemRoutes.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}`
);

export const vendorModelBundleRouteUtil = routeUtilFactory(
  assessmentBundleRoutes,
  `/${vendorModelDetailsRoute.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}`
);

export const vendorAgentBundleRouteUtil = routeUtilFactory(
  assessmentBundleRoutes,
  `/${vendorAgentDetailsRoute.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}`
);

/**
 * Functional Model Bundle route helper utility
 * Manages routes for functional model assessment bundles
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/assessment/:assessmentId/bundle/:bundleId
 * @example
 * // Navigate to bundle form
 * functionalModelBundleRouteUtil.useHelper().navigateTo('BUNDLE_FORM', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     assessmentId: '789',
 *     bundleId: 'b1'
 *   }
 * });
 */
export const functionalModelBundleRouteUtil = routeUtilFactory(
  assessmentBundleRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}`
);

/**
 * Candidate Model Bundle route helper utility
 * Handles routes for candidate model assessment bundles
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/candidate-model/:candidateId/assessment/:assessmentId/bundle/:bundleId
 * @example
 * // Navigate to bundle form
 * candidateModelBundleRouteUtil.useHelper().navigateTo('BUNDLE_FORM', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     candidateId: '789',
 *     assessmentId: 'a1',
 *     bundleId: 'b1'
 *   }
 * });
 */
export const candidateModelBundleRouteUtil = routeUtilFactory(
  assessmentBundleRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateModelDetailsRoute.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}`
);

export const candidateAgentBundleRouteUtil = routeUtilFactory(
  assessmentBundleRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateAgentDetailsRoute.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}`
);

/**
 * Admin route helper utility
 * Manages routes for admin-specific operations and views
 * Base path: /admin
 * @example
 * // Navigate to admin dashboard
 * adminRoutesUtil.useHelper().navigateTo('DASHBOARD');
 * // Navigate to app initialization
 * adminRoutesUtil.useHelper().navigateTo('APPINIT');
 * // Navigate to report template initialization
 * adminRoutesUtil.useHelper().navigateTo('REPORT_TEMPLATE_INIT');
 */
export const adminRoutesUtil = routeUtilFactory(
  adminRoutes,
  `/${adminRoutes.route}/`
);

/**
 * Settings route helper utility
 * Handles routes for application settings and configuration
 * Base path: /settings
 * @example
 * // Navigate to personal settings
 * settingsRoutesUtil.useHelper().navigateTo('PERSONAL');
 * // Navigate to system admin
 * settingsRoutesUtil.useHelper().navigateTo('SYSTEM_ADMIN');
 */
export const settingsRoutesUtil = routeUtilFactory(
  settingRoutes,
  `/${settingRoutes.route}/`
);
/**
 * assessment bundle versions route helper utility
 */
export const assessmentBundleVersionsRouteUtil = routeUtilFactory(
  assessmentBundleVersionRoutes,
  `/${projectRoutes.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}/${assessmentBundleVersionRoutes.route}`
);
/**
 * AI System Assessment Bundle Versions route helper utility
 * Manages routes for AI system assessment bundle version operations
 * Base path: /ai-system/:projectId/assessment/:assessmentId/bundle/:bundleId/answer/:versionId
 * @example
 * // Navigate to a specific version
 * aiSystemAssessmentBundleVersionsRouteUtil.useHelper().navigateTo('VERSIONS_IDS', {
 *   params: {
 *     projectId: '123',
 *     assessmentId: '456',
 *     bundleId: '789',
 *     versionId: 'v1'
 *   }
 * });
 */
export const aiSystemAssessmentBundleVersionsRouteUtil = routeUtilFactory(
  assessmentBundleVersionRoutes,
  `/${aiSystemRoutes.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}/${assessmentBundleVersionRoutes.route}`
);

/**
 * Functional Model Assessment Bundle Versions route helper utility
 * Handles routes for functional model assessment bundle versions
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/assessment/:assessmentId/bundle/:bundleId/answer/:versionId
 * @example
 * // Navigate to version details
 * functionalModelAssessmentBundleVersionsRouteUtil.useHelper().navigateTo('VERSIONS_IDS', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     assessmentId: '789',
 *     bundleId: 'b1',
 *     versionId: 'v1'
 *   }
 * });
 */
export const functionalModelAssessmentBundleVersionsRouteUtil =
  routeUtilFactory(
    assessmentBundleVersionRoutes,
    `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}/${assessmentBundleVersionRoutes.route}`
  );

export const vendorModelAssessmentBundleVersionsRouteUtil = routeUtilFactory(
  assessmentBundleVersionRoutes,
  `/${vendorModelDetailsRoute.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}/${assessmentBundleVersionRoutes.route}`
);

export const vendorAgentAssessmentBundleVersionsRouteUtil = routeUtilFactory(
  assessmentBundleVersionRoutes,
  `/${vendorAgentDetailsRoute.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}/${assessmentBundleVersionRoutes.route}`
);

/**
 * Candidate Model Assessment Bundle Versions route helper utility
 * Manages routes for candidate model assessment bundle versions
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/candidate-model/:candidateId/assessment/:assessmentId/bundle/:bundleId/answer/:versionId
 * @example
 * // Navigate to version details
 * candidateModelAssessmentBundleVersionsRouteUtil.useHelper().navigateTo('VERSIONS_IDS', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     candidateId: '789',
 *     assessmentId: 'a1',
 *     bundleId: 'b1',
 *     versionId: 'v1'
 *   }
 * });
 */
export const candidateModelAssessmentBundleVersionsRouteUtil = routeUtilFactory(
  assessmentBundleVersionRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateModelDetailsRoute.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}/${assessmentBundleVersionRoutes.route}`
);

export const candidateAgentAssessmentBundleVersionsRouteUtil = routeUtilFactory(
  assessmentBundleVersionRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateAgentDetailsRoute.route}/${assessmentRoutes.route}/${assessmentBundleRoutes.route}/${assessmentBundleVersionRoutes.route}`
);
/**
 * Functional Models route helper utility
 * Handles routes for functional model operations
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId
 * @example
 * // Navigate to functional model
 * functionalModelsRouteUtil.useHelper().navigateTo('DASHBOARD', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456'
 *   }
 * });
 */
export const functionalModelsRouteUtil = routeUtilFactory(
  functionalModelsRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}`
);

/**
 * Candidate Models Details route helper utility
 * Manages routes for candidate model details
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/candidate-model/:candidateId
 * @example
 * // Navigate to candidate details
 * candidateModelsDetailsRouteUtil.useHelper().navigateTo('CANDIDATE_DETAILS', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     candidateId: '789'
 *   }
 * });
 */
export const candidateModelsDetailsRouteUtil = routeUtilFactory(
  candidateModelDetailsRoute,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateModelDetailsRoute.route}`
);

/**
 * Candidate Agents Details route helper utility
 * Manages routes for candidate agent details
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/candidate-agent/:agentId
 * @example
 * // Navigate to agent details
 * candidateAgentsDetailsRouteUtil.useHelper().navigateTo('AGENT_DETAILS', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     agentId: '789'
 *   }
 * });
 */
export const candidateAgentsDetailsRouteUtil = routeUtilFactory(
  candidateAgentDetailsRoute,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateAgentDetailsRoute.route}`
);

/**
 * Candidate Model Policy route helper utility
 * Handles routes for candidate model policy operations
 * Base path: /ai-system/:projectId/ai-usecase/:functionalModelId/candidate-model/:candidateId/add-policy
 * @example
 * // Navigate to add policy for candidate model
 * candidateModelPolicyRouteUtil.useHelper().navigateTo('ADD_POLICY', {
 *   params: {
 *     projectId: '123',
 *     functionalModelId: '456',
 *     candidateId: '789'
 *   }
 * });
 */
export const candidateModelPolicyRouteUtil = routeUtilFactory(
  candidateModelPolicyRoutes,
  `/${aiSystemRoutes.route}/${functionalModelsRoutes.route}/${candidateModelDetailsRoute.route}/${candidateModelPolicyRoutes.route}`
);

export const useDashboardHelpers = () => {
  const aiDashboard = aiDashboardUtils.useHelper();
  const functionalModelDashboard = functionalModelDashboardUtils.useHelper();
  const candidateModelDashboard = candidateModelDashboardUtils.useHelper();

  return {
    aiDashboard,
    functionalModelDashboard,
    candidateModelDashboard,
  };
};

/**
 * Vendor Model Assessment Report route helper utility
 * Base path: /vendor-model/:vendorModelId/assessment/:assessmentId/report
 */
export const vendorModelAssessmentReportRouteUtil = routeUtilFactory(
  assessmentReportRoutes,
  `/${vendorModelDetailsRoute.route}/${assessmentRoutes.route}/${assessmentReportRoutes.route}`
);

/**
 * Vendor Agent Assessment Report route helper utility
 * Base path: /vendor-agent/:vendorAgentId/assessment/:assessmentId/report
 */
export const vendorAgentAssessmentReportRouteUtil = routeUtilFactory(
  assessmentReportRoutes,
  `/${vendorAgentDetailsRoute.route}/${assessmentRoutes.route}/${assessmentReportRoutes.route}`
);

export const useAssessmentHelpers = () => {
  // AI System helpers
  const aiSystemHelper = aiSystemAssessmentRouteUtil.useHelper();
  const aiSystemBundleHelper = aiSystemBundleRouteUtil.useHelper();
  const aiSystemVersionHelper =
    aiSystemAssessmentBundleVersionsRouteUtil.useHelper();

  // Functional Model helpers
  const functionalModelHelper = functionalModelAssessmentRouteUtil.useHelper();
  const functionalModelBundleHelper =
    functionalModelBundleRouteUtil.useHelper();
  const functionalModelVersionHelper =
    functionalModelAssessmentBundleVersionsRouteUtil.useHelper();

  // Candidate Model helpers
  const candidateModelHelper = candidateModelAssessmentRouteUtil.useHelper();
  const candidateModelBundleHelper = candidateModelBundleRouteUtil.useHelper();
  const candidateModelVersionHelper =
    candidateModelAssessmentBundleVersionsRouteUtil.useHelper();
  const candidateAgentHelper = candidateAgentAssessmentRouteUtil.useHelper();
  const candidateAgentBundleHelper = candidateAgentBundleRouteUtil.useHelper();
  const candidateAgentVersionHelper =
    candidateAgentAssessmentBundleVersionsRouteUtil.useHelper();

  // Vendor Model helpers
  const vendorModelHelper = vendorModelAssessmentRouteUtil.useHelper();
  const vendorModelBundleHelper = vendorModelBundleRouteUtil.useHelper();
  const vendorModelVersionHelper =
    vendorModelAssessmentBundleVersionsRouteUtil.useHelper();

  // Vendor Agent helpers
  const vendorAgentHelper = vendorAgentAssessmentRouteUtil.useHelper();
  const vendorAgentBundleHelper = vendorAgentBundleRouteUtil.useHelper();
  const vendorAgentVersionHelper =
    vendorAgentAssessmentBundleVersionsRouteUtil.useHelper();

  return {
    aiSystem: {
      assessment: aiSystemHelper,
      bundle: aiSystemBundleHelper,
      version: aiSystemVersionHelper,
    },
    functionalModel: {
      assessment: functionalModelHelper,
      bundle: functionalModelBundleHelper,
      version: functionalModelVersionHelper,
    },
    candidateModel: {
      assessment: candidateModelHelper,
      bundle: candidateModelBundleHelper,
      version: candidateModelVersionHelper,
    },
    candidateAgent: {
      assessment: candidateAgentHelper,
      bundle: candidateAgentBundleHelper,
      version: candidateAgentVersionHelper,
    },
    vendorModel: {
      assessment: vendorModelHelper,
      bundle: vendorModelBundleHelper,
      version: vendorModelVersionHelper,
    },
    vendorAgent: {
      assessment: vendorAgentHelper,
      bundle: vendorAgentBundleHelper,
      version: vendorAgentVersionHelper,
    },
  };
};

export const useAssessmentRoutePrefix = () => {
  const getRoutePrefix = (
    isBundleForm: boolean,
    isVersions: boolean,
    candidateId?: string,
    functionalModelId?: string,
    agentId?: string,
    vendorModelId?: string,
    vendorAgentId?: string
  ) => {
    if (isBundleForm) {
      if (vendorModelId) {
        return vendorModelBundleRouteUtil.routePrefix;
      }
      if (vendorAgentId) {
        return vendorAgentBundleRouteUtil.routePrefix;
      }
      return candidateId
        ? candidateModelBundleRouteUtil.routePrefix
        : agentId
          ? candidateAgentBundleRouteUtil.routePrefix
          : functionalModelId
            ? functionalModelBundleRouteUtil.routePrefix
            : aiSystemBundleRouteUtil.routePrefix;
    }

    if (isVersions) {
      if (vendorModelId) {
        return vendorModelAssessmentBundleVersionsRouteUtil.routePrefix;
      }
      if (vendorAgentId) {
        return vendorAgentAssessmentBundleVersionsRouteUtil.routePrefix;
      }
      return candidateId
        ? candidateModelAssessmentBundleVersionsRouteUtil.routePrefix
        : agentId
          ? candidateAgentAssessmentBundleVersionsRouteUtil.routePrefix
          : functionalModelId
            ? functionalModelAssessmentBundleVersionsRouteUtil.routePrefix
            : aiSystemAssessmentBundleVersionsRouteUtil.routePrefix;
    }

    if (vendorModelId) {
      return vendorModelAssessmentRouteUtil.routePrefix;
    }
    if (vendorAgentId) {
      return vendorAgentAssessmentRouteUtil.routePrefix;
    }
    return candidateId
      ? candidateModelAssessmentRouteUtil.routePrefix
      : agentId
        ? candidateAgentAssessmentRouteUtil.routePrefix
        : functionalModelId
          ? functionalModelAssessmentRouteUtil.routePrefix
          : aiSystemAssessmentRouteUtil.routePrefix;
  };

  return getRoutePrefix;
};

export const useAssessmentVersionHelper = () => {
  const getRouteVersionHelper = (
    aiSystemId?: string,
    candidateId?: string,
    functionalModelId?: string,
    vendorModelId?: string,
    vendorAgentId?: string,
    agentId?: string
  ) => {
    return candidateId
      ? candidateModelAssessmentBundleVersionsRouteUtil
      : agentId
        ? candidateAgentAssessmentBundleVersionsRouteUtil
        : functionalModelId
          ? functionalModelAssessmentBundleVersionsRouteUtil
          : vendorModelId
            ? vendorModelAssessmentBundleVersionsRouteUtil
            : vendorAgentId
              ? vendorAgentAssessmentBundleVersionsRouteUtil
              : aiSystemId
                ? aiSystemAssessmentBundleVersionsRouteUtil
                : candidateModelAssessmentBundleVersionsRouteUtil;
  };

  return getRouteVersionHelper;
};

export const useDashboardHandlers = () => {
  const aiDashboard = aiDashboardUtils.useHelper();
  const functionalModelDashboard = functionalModelDashboardUtils.useHelper();
  const candidateModelDashboard = candidateModelDashboardUtils.useHelper();
  const dashboard = dashboardRouteUtil.useHelper();

  const navigateToMainDashboard = (
    candidateId?: string,
    functionalModelId?: string,
    params?: Record<string, string>,
    vendorModelId?: string,
    vendorAgentId?: string,
    projectType?: ProjectType
  ) => {
    if (candidateId) {
      const route =
        projectType === ProjectType.Dataset
          ? 'DATASET_DETAILS'
          : 'CANDIDATE_DETAILS';
      return candidateModelDashboard.navigateTo(route, {
        params: params ?? {},
      });
    }
    if (functionalModelId) {
      return functionalModelDashboard.navigateTo('DASHBOARD', {
        params: params ?? {},
      });
    }
    if (vendorModelId) {
      return dashboard.navigateTo('VENDOR_MODEL');
    }
    if (vendorAgentId) {
      return dashboard.navigateTo('VENDOR_AGENT');
    }
    return aiDashboard.navigateTo('DASHBOARD', { params: params ?? {} });
  };

  return {
    aiDashboard,
    functionalModelDashboard,
    candidateModelDashboard,
    navigateToMainDashboard,
  };
};
